import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  ShieldCheck, AlertCircle, CheckCircle2,
  Loader2, ArrowLeft, CreditCard, Info,
} from "lucide-react";
import { createTrialSubscription } from "../../dashboard/services/dashboardService";
import { STRIPE_PUBLISHABLE_KEY } from "../../../config/clientConfig";

// Stripe is loaded lazily (only when this component first mounts)
let _stripePromise = null;
const getStripePromise = () => {
  if (!_stripePromise) _stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  return _stripePromise;
};

const CARD_OPTIONS = {
  style: {
    base: {
      fontSize: "15px",
      color: "#1e293b",
      fontFamily: "system-ui, -apple-system, sans-serif",
      fontSmoothing: "antialiased",
      "::placeholder": { color: "#94a3b8" },
    },
    invalid: { color: "#ef4444", iconColor: "#ef4444" },
    complete: { color: "#059669" },
  },
  hidePostalCode: true,
};

const TrialForm = ({
  planId, userName, userEmail,
  planName, planPrice,
  onSuccess, onBack,
  // "trial"   - converting at the end of a free trial (default; existing callers)
  // "upgrade" - subscribing directly from the Subscription page
  // The card is charged immediately either way, but the trial copy promises the
  // customer will NOT be charged, which is false in the upgrade context.
  mode = "trial",
}) => {
  const isUpgrade = mode === "upgrade";
  const stripe   = useStripe();
  const elements = useElements();

  const [cardComplete, setCardComplete] = useState(false);
  const [cardError,    setCardError]    = useState("");
  const [processing,   setProcessing]   = useState(false);
  const [succeeded,    setSucceeded]    = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !cardComplete) return;
    const cardEl = elements.getElement(CardElement);
    if (!cardEl) return;

    setProcessing(true);
    setCardError("");

    try {
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: "card",
        card: cardEl,
        billing_details: { name: userName?.trim(), email: userEmail?.trim() },
      });

      if (pmError) {
        setCardError(pmError.message || "Card verification failed. Please try again.");
        setProcessing(false);
        return;
      }

      await createTrialSubscription({
        plan_id:           planId,
        payment_method_id: paymentMethod.id,
      });

      setSucceeded(true);
      setTimeout(() => onSuccess?.(), 2000);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail  ||
        err?.message                  ||
        "Subscription setup failed. Please try again.";
      setCardError(msg);
      setProcessing(false);
    }
  };

  if (succeeded) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-brand-text mb-1">
          {isUpgrade ? "Subscription Active!" : "Trial Started!"}
        </h3>
        <p className="text-sm text-green-600 font-medium">
          {isUpgrade ? `${planName} — $${planPrice}/month` : `${planName} — 30-Day Free Trial Active`}
        </p>
        <p className="text-xs text-brand-muted mt-2">Redirecting to your dashboard…</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-blue-600 font-bold text-xs leading-none">5/5</span>
        </div>
        <div>
          <p className="font-semibold text-blue-900 text-sm">
            {isUpgrade ? `Subscribe to ${planName}` : "Your free trial has ended"}
          </p>
          <p className="text-blue-700 text-xs mt-0.5 leading-relaxed">
            You have used your 5 free analyses or reached day 30. Add a card to
            keep analysing — <strong>${planPrice}/month</strong>, starting today.
            Your existing reports stay available either way.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-brand-muted mb-1">Name on card</label>
          <div className="px-3 py-2.5 bg-surface border border-surface-border rounded-lg text-sm text-brand-text truncate">
            {userName || "—"}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-brand-muted mb-1">Billing email</label>
          <div className="px-3 py-2.5 bg-surface border border-surface-border rounded-lg text-sm text-brand-text truncate">
            {userEmail || "—"}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-brand-text mb-1.5">Card details</label>
        <div className="border border-surface-border rounded-lg p-3.5 bg-white focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition">
          <CardElement
            options={CARD_OPTIONS}
            onChange={(e) => {
              setCardComplete(e.complete);
              setCardError(e.error?.message || "");
            }}
          />
        </div>
      </div>

      {cardError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {cardError}
        </div>
      )}

      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 leading-relaxed">
          {isUpgrade
            ? `Your card is charged $${planPrice} today, then monthly until cancelled. You can cancel any time from this page.`
            : "You can cancel any time before the trial ends and you will not be charged. After the trial, billing recurs monthly until cancelled."}
        </p>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-brand-muted">
        <ShieldCheck className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
        Secured by Stripe · Encrypted · Card details never stored on our servers
      </div>

      <button
        type="submit"
        disabled={processing || !stripe || !cardComplete}
        className="w-full bg-primary hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        {processing ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> {isUpgrade ? "Processing payment…" : "Setting up trial…"}</>
        ) : (
          <><CreditCard className="w-4 h-4" /> {isUpgrade ? `Pay $${planPrice} and subscribe` : "Add card and continue"}</>
        )}
      </button>

      {/* <button
        type="button"
        onClick={onBack}
        className="w-full flex items-center justify-center gap-1.5 text-sm text-brand-muted hover:text-brand-text transition-colors py-1"
      >
        <ArrowLeft className="w-4 h-4" /> Back to account details
      </button> */}
    </form>
  );
};

const TrialPaymentStep = (props) => (
  <Elements stripe={getStripePromise()}>
    <TrialForm {...props} />
  </Elements>
);

export default TrialPaymentStep;
